import { memo } from 'react';
import cuid from 'cuid';
import { UPLOAD } from '../config';
import { defaultAuth } from '../atoms/auth';
import _ from 'lodash';
import { UploadIcon, XIcon } from '@heroicons/react/outline';
import { notificationAtom } from '../atoms/system';
import { useSetAtom } from 'jotai';
import { Image } from '../types';
import { Updater } from 'use-immer';

const ImageUpload = ({
  action,
}: {
  action(values: Image[]): void;
}): JSX.Element => {
  const id = cuid();
  const notification = useSetAtom(notificationAtom);
  const handleImage = async (event: any) => {
    event.preventDefault();
    try {
      const files = event.target.files;
      if (!_.isObjectLike(files) || files.length < 0) {
        throw Error('Vui lòng chọn file hình ảnh');
      }
      let flagSize = false;
      const images: Image[] = [];
      _.forEach(files, file => {
        if (file.size > 5242880) {
          flagSize = true;
          return false;
        }
        const blob = new Blob([file], { type: file.type });
        const uri = URL.createObjectURL(blob);
        images.push({
          imageId: cuid(),
          src: uri,
          blob: blob,
        });
      });
      if (flagSize) {
        throw Error('Kích thước hình ảnh không được lớn hơn 5mb');
      }
      action(images);
    } catch (e) {
      console.log(e);
      notification({
        show: true,
        type: null,
        title: _.toString(e),
      });
    }
  };

  return (
    <div className="flex w-16 h-16">
      <input
        type="file"
        id={id}
        className="hidden"
        onChange={handleImage}
        accept="image/png,image/gif,image/jpeg,image/jpg"
      />
      <label
        htmlFor={id}
        className="w-full h-full flex flex-col bg-gray-100 border border-gray-200 rounded-md p-3 justify-center items-center cursor-pointer space-y-1"
      >
        <UploadIcon className="w-4 h-4 text-gray-600" />
        <span className="text-gray-600 text-xs">Tải lên</span>
      </label>
    </div>
  );
};

const ImageItem = ({
  image,
  index,
  showOnly,
  action,
}: {
  image: Image;
  index: number;
  showOnly: boolean;
  action: Updater<Image[]> | undefined;
}): JSX.Element => {
  let src = '';
  if (!_.isUndefined(image.blob) || !_.isString(image.imageId)) {
    src = image.src;
  } else {
    src = `${UPLOAD}/files/${defaultAuth?.insider_id}/${image.src}`;
  }
  return (
    <div className="relative w-16 h-16">
      <a
        href={src}
        target="_blank"
        rel="noreferrer"
        className="w-full h-full flex justify-center items-center overflow-hidden rounded-md border border-gray-100"
      >
        <img src={src} alt="" className="object-contain w-full h-full" />
      </a>
      {!showOnly && !_.isUndefined(action) ? (
        <button
          type="button"
          onClick={() => {
            action(draft => {
              draft.splice(index, 1);
            });
          }}
          className="absolute flex justify-center items-center -right-1 -top-1 w-3 h-3 rounded-full bg-red-400 hover:bg-red-500 text-white"
        >
          <XIcon className="w-2 h-2" />
        </button>
      ) : null}
    </div>
  );
};

const ImageList = ({
  max,
  list,
  showOnly = false,
  action,
}: {
  max: number;
  list: Image[];
  showOnly?: boolean;
  action?: Updater<Image[]>;
}): JSX.Element => {
  const handleUpload = (values: Image[]) => {
    if (!showOnly && !_.isUndefined(action)) {
      action(draft => {
        _.forEach(values, value => {
          draft.push(value);
        });
      });
    }
  };
  return (
    <div className="flex flex-wrap space-x-4">
      {list.length > 0 ? (
        <div className="flex flex-wrap space-x-4">
          {list.map((v, i) => (
            <ImageItem
              image={v}
              key={i}
              index={i}
              showOnly={showOnly}
              action={action}
            />
          ))}
        </div>
      ) : null}
      {list.length < max && !showOnly ? (
        <ImageUpload action={handleUpload} />
      ) : null}
    </div>
  );
};

export default memo(ImageList);
